import { useCallback, useEffect, useMemo, useState } from 'react';

import { CustomControls } from './CustomControls';

type Props = React.ComponentProps<typeof CustomControls> & {
  containerRef: React.RefObject<HTMLDivElement>;
};

export function CustomControlsWidget({ containerRef, styles, ...props }: Props) {
  const [style, setStyle] = useState<React.CSSProperties>({ display: 'none' });

  const update = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setStyle({
      position: 'absolute',
      bottom: `-20px`,
      width: `${Math.round(rect.width)}px`,
      // Must stay above the drawing layer (z-index 10): the volume slider and the
      // width/fullscreen menu open upwards over the video.
      zIndex: 20,
      ...styles,
    });
  }, [containerRef, styles]);

  const debouncedUpdate = useMemo(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(update, 16);
    };
  }, [update]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    update();

    const ro = new ResizeObserver(debouncedUpdate);
    ro.observe(el);
    if (el.offsetParent) ro.observe(el.offsetParent as Element);

    let rafId: number | null = null;
    const mo = new MutationObserver(() => {
      if (rafId == null) {
        rafId = requestAnimationFrame(() => {
          rafId = null;
          debouncedUpdate();
        });
      }
    });
    mo.observe(el, {
      childList: true,
      subtree: false,
      attributes: true,
    });

    window.addEventListener('scroll', debouncedUpdate, { passive: true });
    window.addEventListener('resize', debouncedUpdate);

    return () => {
      ro.disconnect();
      mo.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', debouncedUpdate);
      window.removeEventListener('resize', debouncedUpdate);
    };
  }, [containerRef, debouncedUpdate, update]);

  const handleControlsHover = useCallback(
    (isHovered: boolean) => {
      props.setIsControlsHovered(isHovered);
    },
    [props.setIsControlsHovered]
  );

  return (
    <div
      style={style}
      onMouseEnter={() => handleControlsHover(true)}
      onMouseLeave={() => handleControlsHover(false)}
    >
      <CustomControls {...props} />
    </div>
  );
}
